import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { LogOut, MapPin, Users as UsersIcon, Menu, ShieldCheck, Activity, ScrollText, Radar, LayoutGrid, ClipboardList, Building2, CalendarClock, Sparkles, Sun, Moon, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { capacidades } from "@/lib/capacidades";
import { homeDe } from "@/lib/home";
import { useScenario } from "@/context/ScenarioContext";
import { AlertsProvider, useAlerts } from "@/context/AlertsContext";
import GlobalAlertLayer from "@/components/alerts/GlobalAlertLayer";
import IABarra from "@/components/ia/IABarra";
import DemoGuion from "@/components/DemoGuion";
import api from "@/lib/api";
import LogoGalarya from "@/components/LogoGalarya";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { BotonPushIpad } from "@/components/push/BotonPushIpad";
import { limpiarEnLogout } from "@/lib/push/webpush";

// Central oficial de Galarya = /consola-v3. La /consola legacy se retira de la navegación
// operativa (queda sólo como fallback técnico temporal en el router).
// VIS-01: Radar queda ARRIBA de Central (sólo orden visual). Iconos, nombres, rutas, permisos
// y la ruta de aterrizaje tras el login (lib/home.js) NO cambian.
const navItems = [
  { to: "/centro-v3", label: "Radar del Centro V3", icon: LayoutGrid },
  { to: "/consola-v3", label: "Central", icon: Radar },
  { to: "/bitacora", label: "Bitácora de Central", icon: ScrollText },
  { to: "/bitacora-operativa", label: "Bitácora operativa", icon: ClipboardList },
  { to: "/salud", label: "Salud técnica", icon: Activity, soloSupervisor: true },
  { to: "/sitios", label: "Sitios", icon: MapPin },
  { to: "/organizaciones", label: "Organizaciones", icon: Building2, soloSupervisor: true },
  { to: "/programados", label: "Programados", icon: CalendarClock, soloSupervisor: true },
  { to: "/pendientes", label: "Pendientes de alta", icon: ClipboardList, soloSupervisor: true },
  { to: "/usuarios", label: "Usuarios del sistema", icon: UsersIcon, soloSupervisor: true },
  { to: "/ws101-credenciales", label: "Credenciales WS101", icon: KeyRound, soloSupervisor: true },
];

// Gate D: la navegación institucional externa se reduce a lo autorizado. Ocultar es UX;
// el backend sigue siendo la autoridad (toda ruta internal_only responde 403 a un external).
const NAV_EXTERNAL = ["/consola-v3", "/centro-v3", "/sitios", "/bitacora-operativa", "/solicitud"];

function NavContent({ onNavigate, items }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          data-testid={`nav-${to.replace("/", "")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors duration-200 ${
              isActive
                ? "bg-[#01CBE3]/10 dark:bg-[#01CBE3]/20 text-[#012472] dark:text-[#80B3FF]"
                : "text-slate-500 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-[#012472] dark:hover:text-white"
            }`
          }
        >
          <Icon strokeWidth={1.5} className="h-5 w-5" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

// v2.15.2 — control ÚNICO del tema (misma preferencia por navegador para todas las superficies).
// Mismo acceso visual para monitorista y supervisor: no depende del rol.
function TemaToggle({ noche, onToggle, testid, compacto = false }) {
  const label = noche ? "Cambiar a modo claro" : "Cambiar a modo noche";
  const Icono = noche ? Sun : Moon;
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      aria-label={label}
      aria-pressed={!!noche}
      title={label}
      className={
        compacto
          ? "flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 transition-colors hover:bg-slate-100 hover:text-[#012472] dark:text-slate-200 dark:hover:bg-slate-700 dark:hover:text-white"
          : "flex min-h-11 w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-[#012472] dark:text-slate-200 dark:hover:bg-slate-700 dark:hover:text-white"
      }
    >
      <Icono strokeWidth={1.6} className="h-5 w-5" />
      {!compacto && <span>{noche ? "Modo claro" : "Modo noche"}</span>}
    </button>
  );
}

// GC92 · PRIORIDAD DE LA CAPA DE ALARMAS: una ALTA nueva MINIMIZA el panel del asistente (no lo
// cierra ni descarta su conversación, su borrador ni la propuesta preparada). Vive dentro del
// AlertsProvider porque es el único punto que conoce las alarmas sin tomar.
function MinimizarConAlta({ activo, onAlta }) {
  const { altaSinTomar } = useAlerts();
  const n = Array.isArray(altaSinTomar) ? altaSinTomar.length : Number(altaSinTomar || 0);
  const prev = useRef(n);
  useEffect(() => {
    if (activo && n > prev.current) onAlta();
    prev.current = n;
  }, [n, activo, onAlta]);
  return null;
}

export default function Layout() {
  const { user, logout, isSupervisor, isExternal } = useAuth();
  // RBAC-001/UI-003: la etiqueta del ROL sale del rol real de la cuenta (incluidos los
  // institucionales externos), no de un binario supervisor/monitorista.
  const rolLabel = { supervisor: "Supervisor", monitorista: "Monitorista",
    institucional_admin: "Administrador institucional",
    institucional_operador: "Monitorista institucional" }[user?.role]
    || (isSupervisor ? "Supervisor" : "Monitorista");
  const caps = capacidades(user);
  const { isDemo } = useScenario();
  const { noche, toggleNoche } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Asistente: el estado de apertura vive en el shell para poder reservarle su PROPIO espacio en
  // la navegación (antes era un botón `fixed bottom-4 left-4` que caía encima de «Cerrar sesión»).
  const [iaAbierta, setIaAbierta] = useState(false);
  const [iaEstado, setIaEstado] = useState(null);
  const [iaVista, setIaVista] = useState(null);
  const [iaPendientes, setIaPendientes] = useState(0);
  const [version, setVersion] = useState("");
  const [versionNombre, setVersionNombre] = useState("");
  const [sitiosScoped, setSitiosScoped] = useState(null);
  // VIS-01R2 · BOUNDARY DEL TEMA: la clase `dark` se aplica AQUÍ, en el shell autenticado, y se
  // retira al desmontarlo (logout / vuelta al Login). Así el tema sigue alcanzando a los portales
  // de la app (Dialog, Select, Sheet) —que viven en <body>— sin teñir nunca las rutas públicas.
  useEffect(() => {
    const r = document.documentElement;
    if (noche) r.classList.add("dark"); else r.classList.remove("dark");
    return () => r.classList.remove("dark");
  }, [noche]);
  useEffect(() => { api.get("/version").then(({ data }) => { setVersion(data.version); setVersionNombre(data.nombre); }).catch(() => {}); }, []);
  useEffect(() => { api.get("/ia/estado").then((r) => setIaEstado(r.data)).catch(() => setIaEstado(null)); }, []);
  // GC92 · contador DISCRETO de solicitudes revisables por esta cuenta. Refresco ligero: sin LLM
  // (sólo el listado ya existente), cada 60 s y SÓLO con la pestaña activa.
  useEffect(() => {
    if (!iaEstado?.activa || !iaEstado?.puede_revisar) return;
    let vivo = true;
    const leer = () => {
      if (document.visibilityState !== "visible") return;
      api.get("/ia/act/pendientes")
        .then((r) => { if (vivo) setIaPendientes(r.data?.total ?? (r.data?.solicitudes || []).length); })
        .catch(() => { if (vivo) setIaPendientes(0); });
    };
    leer();
    const t = setInterval(leer, 60000);
    document.addEventListener("visibilitychange", leer);
    return () => { vivo = false; clearInterval(t); document.removeEventListener("visibilitychange", leer); };
  }, [iaEstado]);
  // Home del usuario externo: se resuelve con los sitios que el BACKEND ya devuelve scoped
  // (effective sites), nunca con allowed_site_ids en bruto.
  useEffect(() => {
    if (!isExternal) return;
    api.get("/sitios").then((r) => setSitiosScoped(r.data || [])).catch(() => setSitiosScoped([]));
  }, [isExternal]);

  const irAHome = async (cerrarDrawer) => {
    if (cerrarDrawer) setOpen(false);
    if (!isExternal) { navigate(homeDe(user)); return; }
    // v2.14.0 — la cuenta institucional vuelve a SU centro (Radar Operativo · Institucional).
    let lista = sitiosScoped;
    if (lista === null) {
      lista = await api.get("/sitios").then((r) => r.data || []).catch(() => []);
      setSitiosScoped(lista);
    }
    const activos = lista.filter((s) => s.estado !== "Inactivo");
    navigate(activos.length === 1 ? `/centro-v3/${activos[0].id}` : "/centro-v3");
  };
  const fullBleed = location.pathname.startsWith("/consola") || location.pathname.startsWith("/centro-v3");

  // DEMO: la navegación se confina a superficies demo-seguras. Las rutas que leen datos reales
  // (Sitios, Usuarios, Salud, consola legacy) se ocultan del menú y se redirigen si se abren directo.
  const DEMO_ROUTES = ["/consola-v3", "/centro-v3", "/bitacora"];
  const enRutaDemo = (to) => DEMO_ROUTES.some((p) => to.startsWith(p));
  const visibleItems = navItems.filter(
    (it) =>
      (!it.soloSupervisor || caps.supervisor) &&
      (!isDemo || enRutaDemo(it.to)) &&
      (!isExternal || NAV_EXTERNAL.some((p) => it.to.startsWith(p))) &&
      // GC106 · la monitorista residencial ve UNA sola Bitácora. La operativa sigue existiendo
      // (ruta, expediente, reportes y enlaces directos intactos): sólo sale de SU menú.
      !(it.to === "/bitacora-operativa" && !caps.supervisor && !isExternal)
  ).map((it) =>
    // v2.13.3: el monitorista interno recorre PERSONAS del universo residencial, no centros.
    // v2.14.0: y la cuenta institucional entra a SU centro. En ambos casos la superficie principal
    // se llama «Radar Operativo»; el supervisor conserva su etiqueta institucional.
    it.to === "/centro-v3" && !caps.supervisor ? { ...it, label: "Radar Operativo" } : it
  ).map((it) =>
    // Con una sola Bitácora a la vista, la monitorista no necesita el apellido «de Central».
    it.to === "/bitacora" && !caps.supervisor ? { ...it, label: "Bitácora" } : it
  );
  const blockedInDemo = isDemo && !enRutaDemo(location.pathname);
  const blockedExternal =
    isExternal && !NAV_EXTERNAL.some((p) => location.pathname.startsWith(p));
  useEffect(() => { if (blockedInDemo) navigate("/consola-v3", { replace: true }); }, [blockedInDemo, navigate]);
  useEffect(() => { if (blockedExternal) navigate("/consola-v3", { replace: true }); }, [blockedExternal, navigate]);

  const doLogout = async () => {
    // NOTIF-INST-01 · la baja del dispositivo se hace CON la sesión todavía válida (el token se
    // borra en logout()). Nunca bloquea el cierre: a los 3 s se continúa pase lo que pase.
    await Promise.race([limpiarEnLogout(), new Promise((r) => setTimeout(r, 3000))]);
    logout();
    navigate("/login");
  };

  return (
    <AlertsProvider>
    <div className="h-screen overflow-hidden flex bg-[#F9FAFB] dark:bg-slate-950">
      <GlobalAlertLayer />
      <MinimizarConAlta activo={iaAbierta} onAlta={() => setIaAbierta(false)} />
      {/* Barra de lenguaje natural: hermana del shell, FUERA del wrapper `.galarya-fade` (su
          transform crearía un containing block y rompería el posicionamiento fixed). */}
      <IABarra estado={iaEstado} abierta={iaAbierta} onCerrar={() => setIaAbierta(false)} vistaSolicitada={iaVista} />
      <DemoGuion />
      {/* Sidebar escritorio — fija al viewport (no desaparece al hacer scroll del contenido). */}
      <aside data-testid="sidebar-desktop" className="hidden md:flex md:flex-col items-center w-16 shrink-0 border-r border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-900 py-4">
        <div className="flex shrink-0 flex-col items-center mb-4">
          <button type="button" onClick={() => irAHome(false)} data-testid="logo-home-desktop"
            aria-label="Ir al inicio" title="Ir al inicio"
            className="flex h-12 w-12 items-center justify-center rounded-xl cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01CBE3]">
            {/* VIS-01R2 · ÚNICO logo Galarya de la app autenticada (los subheaders de Radar y
                Central ya no lo repiten: usan el icono y el nombre de su módulo).
                LOGO-26 · la barra mide 64 px: aquí va el SÍMBOLO del logo horizontal (el lema no
                sería legible a este tamaño); el logo completo vive en móvil, menú y portada. */}
            <LogoGalarya variante="simbolo" className="h-10 w-10" />
          </button>
          {version && <span data-testid="app-version" title={versionNombre} className="mt-1 text-[9px] font-semibold text-slate-400 dark:text-slate-500">v{version}</span>}
        </div>
        {/* La navegación es la única zona que scrollea: el asistente, el tema y «Cerrar sesión»
            quedan SIEMPRE visibles en la zona inferior, incluso con zoom o ventana reducida. */}
        <nav className="flex min-h-0 flex-1 flex-col items-center gap-2 overflow-y-auto">
          {visibleItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${to.replace("/", "")}`}
              title={label}
              aria-label={label}
              className={({ isActive }) =>
                `flex h-11 w-11 items-center justify-center rounded-xl transition-colors duration-200 ${
                  isActive
                    ? "bg-[#01CBE3]/15 dark:bg-[#01CBE3]/25 text-[#012472] dark:text-[#80B3FF]"
                    : "text-slate-400 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-[#012472] dark:hover:text-white"
                }`
              }
            >
              <Icon strokeWidth={1.6} className="h-5 w-5" />
            </NavLink>
          ))}
        </nav>
        {/* Bloque PROPIO del asistente: separado del bloque de usuario/Cerrar sesión, así ambos
            quedan visibles y accionables con el drawer abierto o cerrado (sin z-index ni overlays). */}
        {iaEstado?.activa && (
          <div className="mt-3 w-full shrink-0 border-t border-gray-100 pt-3 dark:border-slate-700">
            <div className="flex justify-center">
              <button
                type="button"
                data-testid="ia-barra-abrir"
                onClick={() => { setIaVista(null); setIaAbierta(true); }}
                aria-expanded={iaAbierta}
                aria-label="Preguntar a Galarya"
                title="Preguntar a Galarya — consulta o pide algo en lenguaje natural"
                className={`relative flex h-11 w-11 items-center justify-center rounded-xl border transition-colors ${
                  iaAbierta
                    ? "border-[#01CBE3] bg-[#01CBE3]/15 text-[#012472] dark:bg-[#01CBE3]/25 dark:text-white"
                    : "border-slate-200 text-[#012472] hover:border-[#01CBE3] hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-700"
                }`}
              >
                <Sparkles className="h-5 w-5 text-[#01CBE3]" strokeWidth={2} />
                {iaPendientes > 0 && (
                  <span data-testid="ia-pendientes-contador"
                    title={`${iaPendientes} solicitud(es) que tu cuenta puede revisar`}
                    className="absolute -right-1 -top-1 min-w-4 rounded-full bg-[#012472] px-1 text-[9px] font-bold leading-4 text-white">
                    {iaPendientes > 9 ? "9+" : iaPendientes}
                  </span>
                )}
              </button>
            </div>
            {iaPendientes > 0 && (
              <button type="button" data-testid="ia-pendientes-acceso"
                onClick={() => { setIaVista("pendientes"); setIaAbierta(true); }}
                title="Ver las solicitudes pendientes que puedes revisar"
                className="mx-auto mt-1 block text-[9px] font-bold text-[#0891A6] hover:underline">
                Revisar
              </button>
            )}
          </div>
        )}
        <div className="mt-3 flex w-full shrink-0 flex-col items-center gap-2 border-t border-gray-100 pt-3 dark:border-slate-700">
          <TemaToggle noche={noche} onToggle={toggleNoche} testid="theme-toggle-desktop" compacto />
          {/* NOTIF-INST-01 · sólo la cuenta institucional registra dispositivos de enfermería. */}
          {isExternal && <BotonPushIpad compacto />}
          {/* RBAC-001/UI-003: el nombre de la cuenta y el ROL efectivo se enuncian por separado;
              «Administrador Galarya» es una identidad, no un rol. Roles: Supervisor | Monitorista. */}
          <span data-testid="perfil-avatar"
            title={`${user?.nombre || user?.email} — Rol: ${rolLabel}`}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#012472]/10 dark:bg-slate-700 text-[#012472] dark:text-slate-100 text-xs font-semibold">
            {(user?.nombre || user?.email || "?").trim().charAt(0).toUpperCase()}
          </span>
          <button
            onClick={doLogout}
            data-testid="logout-button"
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
            className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 hover:text-[#012472] hover:bg-slate-50 dark:text-slate-200 dark:hover:text-white dark:hover:bg-slate-700"
          >
            <LogOut strokeWidth={1.5} className="h-4 w-4" />
          </button>
        </div>
      </aside>

      {/* Contenido */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar móvil */}
        <header className="md:hidden sticky top-0 z-20 flex items-center justify-between border-b border-gray-100 dark:border-slate-700 bg-white/80 dark:bg-slate-900/95 backdrop-blur px-4 py-3">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => irAHome(true)} data-testid="logo-home-mobile"
              aria-label="Ir al inicio" title="Ir al inicio"
              className="flex min-h-11 min-w-11 items-center justify-center rounded-xl px-1 cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01CBE3]">
              <LogoGalarya className="h-8 w-auto max-w-[9.5rem]" />
            </button>
            {version && <span data-testid="app-version-mobile" title={versionNombre} className="text-[10px] font-semibold text-slate-400 dark:text-slate-300">v{version}</span>}
          </div>
          <div className="flex items-center gap-1">
            {/* GC92 · acceso al asistente en MÓVIL: antes sólo existía en la barra de escritorio. */}
            {iaEstado?.activa && (
              <button type="button" data-testid="ia-barra-abrir-mobile"
                onClick={() => { setIaVista(null); setIaAbierta(true); }}
                aria-expanded={iaAbierta} aria-label="Preguntar a Galarya"
                title="Preguntar a Galarya"
                className="relative flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-slate-200 text-[#012472] transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-700">
                <Sparkles className="h-5 w-5 text-[#01CBE3]" strokeWidth={2} />
                {iaPendientes > 0 && (
                  <span data-testid="ia-pendientes-contador-mobile"
                    className="absolute right-0 top-0 min-w-4 rounded-full bg-[#012472] px-1 text-[9px] font-bold leading-4 text-white">
                    {iaPendientes > 9 ? "9+" : iaPendientes}
                  </span>
                )}
              </button>
            )}
            <TemaToggle noche={noche} onToggle={toggleNoche} testid="theme-toggle-mobile" compacto />
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-button"
                  aria-label="Abrir menú de navegación" title="Menú"
                  className="text-[#012472] hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-700">
                  <Menu strokeWidth={2} className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" data-testid="mobile-menu-panel"
                className="w-72 border-r border-gray-100 bg-white p-0 dark:border-slate-700 dark:bg-slate-900">
                <SheetTitle className="sr-only">Menú de navegación</SheetTitle>
                <div className="px-6 py-7">
                  <button type="button" onClick={() => irAHome(true)} data-testid="logo-home-drawer"
                    aria-label="Ir al inicio" title="Ir al inicio"
                    className="flex min-h-11 items-center rounded-xl cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01CBE3]">
                    <LogoGalarya className="h-11 w-auto max-w-[13rem]" />
                  </button>
                </div>
                <NavContent onNavigate={() => setOpen(false)} items={visibleItems} />
                <div className="mt-4 px-3">
                  <TemaToggle noche={noche} onToggle={toggleNoche} testid="theme-toggle-drawer" />
                </div>
                {isExternal && (
                  <div className="mt-2 px-3">
                    <BotonPushIpad />
                  </div>
                )}
                <div className="mt-4 border-t border-gray-100 p-4 dark:border-slate-700">
                  <p data-testid="perfil-identidad" className="px-2 text-sm font-medium text-[#012472] dark:text-slate-100">{user?.nombre || user?.email}</p>
                  <p data-testid="perfil-rol" className="mb-2 px-2 text-xs text-slate-500 dark:text-slate-300">Rol: {rolLabel}</p>
                  <Button variant="ghost" onClick={doLogout} data-testid="logout-button-mobile"
                    className="w-full justify-start text-slate-600 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700 dark:hover:text-white">
                    <LogOut strokeWidth={1.5} className="h-4 w-4 mr-2" /> Cerrar sesión
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </header>

        {/* Aviso GLOBAL permanente en DEMO — visible en TODAS las rutas demo (desktop/mobile/fullBleed). */}
        {isDemo && (
          <div data-testid="layout-demo-banner" className="shrink-0 z-30 flex items-center justify-center gap-2 border-b border-[#9B2C2C]/30 bg-[#9B2C2C]/10 dark:bg-[#9B2C2C]/25 px-4 py-1 text-center text-[11px] font-bold uppercase tracking-wide text-[#9B2C2C] dark:text-[#F2B8B8]">
            DEMOSTRACIÓN · DATOS FICTICIOS · NO SE MUESTRA MONITOREO REAL
          </div>
        )}

        <main className={fullBleed ? "flex-1 min-h-0 overflow-hidden" : "flex-1 overflow-y-auto"}>
          {fullBleed ? (
            <Outlet />
          ) : (
            <div className="mx-auto max-w-6xl px-5 py-8 pb-28 md:px-10 md:py-10 md:pb-16 galarya-fade">
              <Outlet />
            </div>
          )}
        </main>
      </div>
    </div>
    </AlertsProvider>
  );
}
